/**
 * Pure utility functions for the Associate View Page (a.k.a. "Labor board").
 *
 * All functions in this module are pure: they take their inputs and return a
 * new value without mutating anything. This keeps the page and row components
 * thin renderers and lets the correctness properties target well-scoped pure
 * code (see `associateView.property.test.ts`).
 */

import { AssignmentAssociate, ProcessPath, PathType, StaffingMode } from '../assignments/models';
import { formatDisplayName } from '../utils';

/**
 * Derive the station label shown on the labor board for a single associate.
 *
 * Walks `processPaths` in order and returns:
 *  - `` `${processPathName} ${workstationName}` `` if the associate is
 *    assigned to a workstation on a PROCESS_LINES path;
 *  - `processPathName` if the associate is in a DEPARTMENT path's
 *    workstations[0].assignedAssociates;
 *  - `"Unassigned"` otherwise.
 *
 * Matching is by `associateId`. The function is pure and does not mutate
 * its inputs.
 */
export function deriveStationLabel(
  associate: AssignmentAssociate,
  processPaths: ProcessPath[]
): string {
  for (const path of processPaths) {
    if (path.staffingMode === StaffingMode.PROCESS_LINES) {
      for (const workstation of path.workstations) {
        if (
          workstation.assignedAssociates.some(
            a => a.associateId === associate.associateId
          )
        ) {
          return `${path.processPathName} ${workstation.workstationName}`;
        }
      }
    }
  }

  for (const path of processPaths) {
    if (path.staffingMode === StaffingMode.DEPARTMENT) {
      for (const workstation of path.workstations) {
        if (
          workstation.assignedAssociates.some(
            a => a.associateId === associate.associateId
          )
        ) {
          return path.processPathName;
        }
      }
    }
  }

  return 'Unassigned';
}

/**
 * Distribute `items` into exactly `columnCount` subarrays in row-major
 * (CSS Grid `grid-auto-flow: row`) order.
 *
 * Concatenating the result row-by-row — for each row index
 * `r = 0, 1, …, maxColumnLength - 1`, emit `result[0][r]`, `result[1][r]`,
 * …, `result[columnCount - 1][r]`, skipping columns where the row index is
 * out of bounds — yields `items` back in order. Adjacent column sizes
 * differ by at most one.
 *
 * For `columnCount <= 0` this function throws a `RangeError`, which is
 * consistent with the typical invariant "there must be at least one
 * column" and surfaces bugs in callers rather than silently returning an
 * empty array.
 *
 * Examples:
 *   distributeIntoColumns([1,2,3,4,5], 2) === [[1,3,5], [2,4]]
 *   distributeIntoColumns([],          3) === [[], [], []]
 *   distributeIntoColumns([1],         4) === [[1], [], [], []]
 *   distributeIntoColumns([1,2,3,4],   2) === [[1,3], [2,4]]
 */
export function distributeIntoColumns<T>(items: T[], columnCount: number): T[][] {
  if (columnCount <= 0) {
    throw new RangeError(
      `distributeIntoColumns: columnCount must be >= 1 (received ${columnCount})`
    );
  }

  const columns: T[][] = Array.from({ length: columnCount }, () => []);
  for (let i = 0; i < items.length; i++) {
    columns[i % columnCount].push(items[i]);
  }
  return columns;
}

/**
 * Case-insensitive sort comparator that orders two associates by their
 * formatted display name (see `formatDisplayName`).
 *
 * Intended for use with `Array.prototype.sort`, e.g.
 *   `[...associates].sort(compareByDisplayName)`.
 *
 * Comparison is lexicographic on the lowercased formatted name. Associates
 * whose formatted names compare equal under this ordering are left in their
 * relative input order by stable sort implementations.
 */
export function compareByDisplayName(
  a: AssignmentAssociate,
  b: AssignmentAssociate
): number {
  const aKey = formatDisplayName(a.associateName).toLowerCase();
  const bKey = formatDisplayName(b.associateName).toLowerCase();
  if (aKey < bKey) return -1;
  if (aKey > bKey) return 1;
  return 0;
}
